import { Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AppTitleService implements OnDestroy {
  private readonly baseTitle = 'Prose';
  private currentPageTitle = 'Dashboard';
  private titleObserver?: MutationObserver;
  private destroy$ = new Subject<void>();
  
  // Route to title mapping
  private routeTitleMap: { [key: string]: string } = {
    '/': 'Dashboard',
    '/userdashboard': 'Dashboard',
    '/section/project-management': 'Project Management',
    '/configuration': 'Configuration',
    '/dropdownConfig': 'Dropdown Configuration',
    '/addform': 'Add Form',
    '/editform': 'Edit Form',
    '/form': 'Form',
    '/dynamic-reports': 'Dynamic Reports',
    '/create-project': 'Create Project',
    '/nav-items': 'Navigation',
    '/routes': 'Routes',
    '/userprofiles': 'User Profiles',
    // Administration routes
    '/identity/users': 'Users',
    '/identity/roles': 'Roles',
    '/setting-management': 'Settings',
    '/tenant-management': 'Tenants'
  };

  constructor(
    private titleService: Title,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  /**
   * Initialize dynamic title updates based on route changes
   */
  initializeDynamicTitles() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.getPageTitle())
      )
      .subscribe(pageTitle => {
        this.currentPageTitle = pageTitle;
        this.setTitle(pageTitle);
        // Add a delayed check to ensure title is not overridden
        setTimeout(() => this.enforceTitle(pageTitle), 100);
        setTimeout(() => this.enforceTitle(pageTitle), 500);
        setTimeout(() => this.enforceTitle(pageTitle), 1000);
      });

    // Set initial title
    const initialTitle = this.getPageTitle();
    this.currentPageTitle = initialTitle;
    this.setTitle(initialTitle);
    
    // Enforce initial title with delays
    setTimeout(() => this.enforceTitle(initialTitle), 100);
    setTimeout(() => this.enforceTitle(initialTitle), 500);
    setTimeout(() => this.enforceTitle(initialTitle), 1000);
    
    // Start continuous monitoring
    this.startTitleMonitoring();
  }

  /**
   * Start monitoring title changes and correct them
   */
  private startTitleMonitoring() {
    // Use MutationObserver to watch for any changes to the title element
    const titleElement = document.querySelector('title');
    if (titleElement) {
      this.titleObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const expectedTitle = `${this.baseTitle} - ${this.currentPageTitle}`;
            const currentTitle = document.title;
            if (currentTitle !== expectedTitle && !currentTitle.startsWith(this.baseTitle)) {
              this.titleService.setTitle(expectedTitle);
            }
          }
        });
      });

      this.titleObserver.observe(titleElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    // Also use interval-based checking as backup
    const checkInterval = setInterval(() => {
      const expectedTitle = `${this.baseTitle} - ${this.currentPageTitle}`;
      const currentTitle = document.title;
      if (currentTitle !== expectedTitle && !currentTitle.startsWith(this.baseTitle)) {
        this.titleService.setTitle(expectedTitle);
      }
    }, 2000); // Check every 2 seconds

    // Clean up interval when service is destroyed
    this.destroy$.subscribe(() => {
      clearInterval(checkInterval);
    });
  }

  /**
   * Set title with Prose prefix
   */
  setTitle(pageTitle: string = 'Dashboard') {
    const fullTitle = `${this.baseTitle} - ${pageTitle}`;
    this.titleService.setTitle(fullTitle);
  }

  /**
   * Enforce title if it has been changed
   */
  private enforceTitle(pageTitle: string) {
    const expectedTitle = `${this.baseTitle} - ${pageTitle}`;
    const currentTitle = document.title;
    if (currentTitle !== expectedTitle) {
      this.titleService.setTitle(expectedTitle);
    }
  }

  /**
   * Clean up resources
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.titleObserver) {
      this.titleObserver.disconnect();
    }
  }

  /**
   * Get the current page title based on route
   */
  private getPageTitle(): string {
    const url = this.router.url;
    
    // Check for exact matches first
    if (this.routeTitleMap[url]) {
      return this.routeTitleMap[url];
    }

    // Check for partial matches (for dynamic routes)
    for (const route in this.routeTitleMap) {
      if (url.startsWith(route) && route !== '/') {
        return this.routeTitleMap[route];
      }
    }

    // Try to get title from route data
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    
    if (route.snapshot.data['title']) {
      return route.snapshot.data['title'];
    }

    // Fallback: extract page name from URL
    const segments = url.split('/').filter(segment => segment);
    if (segments.length > 0) {
      const lastSegment = segments[segments.length - 1];
      // Convert kebab-case to Title Case
      return this.kebabToTitleCase(lastSegment);
    }

    return 'Dashboard';
  }

  /**
   * Convert kebab-case to Title Case
   */
  private kebabToTitleCase(str: string): string {
    return str
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Add or update a route title mapping
   */
  addRouteTitle(route: string, title: string) {
    this.routeTitleMap[route] = title;
  }

  /**
   * Get all route title mappings
   */
  getRouteTitleMap() {
    return { ...this.routeTitleMap };
  }
}
